import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 } from "uuid";

// Types & enums
import DynamicForm from "../../../../shared/commonDynamicForm/CommonDynamicForm";
import { IFarmerQuestionnaireProps } from "./FarmerQuestionnaireTypes";
import {
  EErrorCodes,
  EFarmerTabs,
} from "../../../../../components/shared/commonTypes/commonTypes";

// API Manager
import { getApi, postApi } from "../../../../../services/apiManager/ApiManager";

// Hooks
import { useToaster } from "../../../../../hooks/useToasterContext/useToasterContext";

// Multilingual support
import { t } from "i18next";

// Components
import Loader from "../../../../../components/layout/Loader";

const FarmerQuestionnaire: React.FC<IFarmerQuestionnaireProps> = ({
  setValidateStep,
  newlyAddedFarmerId,
}) => {
  const [farmerComplianceFormData, setFarmerComplianceFormData] =
    useState<any>(null);
  const [isNotHaveComplianceFormResponse, setIsNotHaveComplianceFormResponse] =
    useState<any>(true);
  const [isLoading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Toaster and loader
  const { setShowToaster } = useToaster();

  // Fetch farmer details
  const fetchInitialFarmerComplianceForm = async (
    farmerComplianceId?: string,
  ) => {
    setIsNotHaveComplianceFormResponse(false);
    setLoading(true); // Start loading
    try {
      const response = !farmerComplianceId
        ? await getApi(`v1/questionnaire/form/FARMER_Q`)
        : await getApi(
            `v1/questionnaire/response?entityId=${farmerComplianceId}&formCategory=FARMER_Q`,
          );
      if (response.status) {
        const farmerDetails = response.data;

        if (farmerDetails) {
          setFarmerComplianceFormData(farmerDetails);
        }
        setLoading(false); // ✅ Done loading
      } else {
        if (
          !(farmerComplianceId && farmerComplianceId.length) &&
          response.code === EErrorCodes.E_FORM_NOT_FOUND
        ) {
          navigate("/farmers?selectedActiveTab=" + EFarmerTabs.ENTITY_FARMER);
          return; // 🔁 Exit early to avoid setting loading to false unnecessarily
        } else if (response.code === ("E_FORM_RESPONSE_NOT_FOUND" as any)) {
          fetchInitialFarmerComplianceForm();
          setIsNotHaveComplianceFormResponse(true);
        } else {
          setLoading(false); // Unknown error
        }
      }
    } catch (error) {
      setShowToaster(true, t("commonMessages.internalServerError"), "error");
      setLoading(false); // Prevent stuck loading
    }
  };

  console.log("newlyAddedFarmerId", newlyAddedFarmerId);

  useEffect(() => {
    if (newlyAddedFarmerId) {
      fetchInitialFarmerComplianceForm(newlyAddedFarmerId);
    } else {
      fetchInitialFarmerComplianceForm();
    }
  }, [newlyAddedFarmerId]);

  // Generate UUID
  const generateUUID = () => {
    return v4();
  };

  // Set each answerId to valid GUID
  const setAnswerIdToValidUUID = (formData: any) => {
    if (!formData.sections) return formData; // Ensure sections exist

    formData.sections.forEach((section: any) => {
      if (section.questions) {
        section.questions.forEach((question: any) => {
          // Generate a new UUID if answerId is not present
          if (!question.answerId) {
            question.answerId = generateUUID();
          }
        });
      }
    });
    return formData;
  };

  // Update compliance form api call
  const updateComplianceForm = async (complianceFormValues: any) => {
    if (!farmerComplianceFormData) return;
    const payload = {
      formId: isNotHaveComplianceFormResponse
        ? farmerComplianceFormData.id
        : farmerComplianceFormData.formId,
      formVersion: isNotHaveComplianceFormResponse
        ? farmerComplianceFormData.version
        : farmerComplianceFormData.formVersion,
      formName: farmerComplianceFormData.formName,
      formCategory: "FARMER_Q",
      sections: setAnswerIdToValidUUID(complianceFormValues.formData).sections,
      id: isNotHaveComplianceFormResponse ? null : farmerComplianceFormData.id,
      entityId: isNotHaveComplianceFormResponse
        ? newlyAddedFarmerId
        : farmerComplianceFormData.entityId,
      isDeleted: false,
    };
    try {
      const response = await postApi(`v1/questionnaire/response`, payload);

      if (response.status) {
        setShowToaster(
          true,
          t(
            "addUpdateFarmer.farmerQuestionnaireInfo.complianceFormSubmittedSuccessfully",
          ),
          "success",
        );
        // navigate to farmer list
        navigate("/farmers?selectedActiveTab=" + EFarmerTabs.ENTITY_FARMER);
      }
    } catch (error) {
      setShowToaster(true, t("commonMessages.internalServerError"), "error");
    }
  };
  return (
    <Box sx={{ padding: 2 }}>
      {isLoading && <Loader />}
      {!isLoading && farmerComplianceFormData && (
        <DynamicForm
          formData={farmerComplianceFormData as any}
          onSubmit={(values) => updateComplianceForm(values)}
          setValidateStep={setValidateStep}
          isHideSubmitBtn={true}
          sxControlStyle={{ width: "100%" }}
        />
      )}
    </Box>
  );
};

export default FarmerQuestionnaire;
